import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './useAuth';
import { UserProfile } from '../types/auth.types';

export function useUser() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    const fetchProfile = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;
        setProfile(data);
      } catch (err: unknown) {
        console.error('Error fetching user profile:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // #140: keyed on the user *id*, never on the user object. Supabase replaces
    // `session.user` on every silent token refresh — which is what a tab regaining
    // focus triggers — so keyed on the object this refetched and published a new
    // `profile` object for the same account. UserProfilePage's `[profile]` effect
    // then reset its form field from the saved value, wiping a name the user was
    // half way through typing. `user.id` is the only field read below, so the
    // narrower key is also the complete one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      setProfile(prev => (prev ? { ...prev, ...updates } : null));
      return { error: null };
    } catch (err: unknown) {
      console.error('Error updating profile:', err);
      return { error: err };
    }
  };

  return {
    profile,
    loading,
    error,
    updateProfile,
    isAdmin: profile?.is_system_admin ?? false,
  };
}
